import { localToday } from '../../lib/flightFilters';
import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Wrench, Plus, Check, Trash2, Pencil, FileText, X } from 'lucide-react';
import { db, storage } from '../../lib/firebase';
import { annualDueFrom, mxStatus, mxSummary, isDeferred, MX_LABEL, MX_TONE, MX_PRESETS } from '../../lib/maintenance';
import type { AircraftDoc, MaintenanceItemDoc, MaintenanceTrack } from '../../lib/types';
import { DEMO_MAINTENANCE } from '../../lib/demoData';
import { DateField } from '../../components/DateField';
import { ModalClose } from '../../components/ModalClose';
import { FilePicker } from '../../components/FilePicker';

const TONE_CLS: Record<'ok' | 'warning' | 'danger', string> = {
  ok: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
};
// Local, not UTC: marking an item done after 5pm Pacific used to stamp tomorrow,
// which then shifted every interval computed from it.
const todayStr = () => localToday();
const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export function MaintenanceSection({
  aircraft,
  demoMode,
  onAircraftPatch,
}: {
  aircraft?: AircraftDoc;
  demoMode?: boolean;
  /** Demo only: the page's local aircraft list (live reads the snapshot). */
  onAircraftPatch?: (patch: Partial<AircraftDoc>) => void;
}) {
  const [items, setItems] = useState<MaintenanceItemDoc[]>([]);
  const [editing, setEditing] = useState<MaintenanceItemDoc | 'new' | null>(null);

  useEffect(() => {
    if (!aircraft) return;
    if (demoMode) {
      setItems(DEMO_MAINTENANCE);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'maintenance'), where('aircraftId', '==', aircraft.aircraftId)),
      (snap) => setItems(snap.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id })))
    );
    return () => unsub();
    // Keyed on the id: a patch to the aircraft (e.g. its annual date) must not
    // reload the list and throw away what was just marked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft?.aircraftId, demoMode]);

  if (!aircraft) return null;
  const tach = aircraft.currentTach; // maintenance tracks on tach; dueHobbs/lastDoneHobbs hold tach readings
  const acId = aircraft.aircraftId;
  const rank = (m: MaintenanceItemDoc) => (isDeferred(m) ? -1 : mxStatus(m, tach).level);
  const sorted = [...items].sort((a, b) => rank(b) - rank(a));

  // Keep the aircraft's annual date in step with the Annual item (see annualDueFrom).
  async function syncAnnual(item: { label?: string }, dueDate?: string | null) {
    const due = annualDueFrom(item, dueDate);
    if (!due || due === aircraft?.annualDueDate) return;
    onAircraftPatch?.({ annualDueDate: due });
    if (demoMode || !db) return;
    await updateDoc(doc(db, 'aircraft', acId), { annualDueDate: due });
  }

  async function markDone(item: MaintenanceItemDoc) {
    const patch: Partial<MaintenanceItemDoc> = {
      lastDoneDate: todayStr(),
      lastDoneHobbs: tach,
      lastAlertLevel: 0,
    };
    if (item.intervalDays && (item.track === 'date' || item.track === 'both'))
      patch.dueDate = addDays(todayStr(), item.intervalDays);
    if (item.intervalHours && (item.track === 'hours' || item.track === 'both'))
      patch.dueHobbs = +(tach + item.intervalHours).toFixed(1);
    if (demoMode || !db) {
      setItems((cur) =>
        cur.map((i) =>
          i.itemId === item.itemId ? { ...i, ...patch, deferredUntil: undefined, deferReason: undefined } : i
        )
      );
      await syncAnnual(item, patch.dueDate);
      return;
    }
    await updateDoc(doc(db, 'maintenance', item.itemId), {
      ...patch,
      deferredUntil: deleteField(),
      deferReason: deleteField(),
      updatedAt: serverTimestamp(),
    });
    await syncAnnual(item, patch.dueDate);
  }

  async function remove(item: MaintenanceItemDoc) {
    if (demoMode || !db) {
      setItems((cur) => cur.filter((i) => i.itemId !== item.itemId));
      return;
    }
    await deleteDoc(doc(db, 'maintenance', item.itemId));
  }

  async function save(data: Partial<MaintenanceItemDoc>, existing?: MaintenanceItemDoc) {
    if (demoMode || !db) {
      if (existing) setItems((cur) => cur.map((i) => (i.itemId === existing.itemId ? { ...i, ...data } : i)));
      else setItems((cur) => [...cur, { ...(data as MaintenanceItemDoc), itemId: `demo-${Date.now()}` }]);
      await syncAnnual({ label: data.label ?? existing?.label }, data.dueDate);
      setEditing(null);
      return;
    }
    // Firestore is initialized without `ignoreUndefinedProperties`, so an undefined
    // value throws on write instead of clearing the field. Optional fields the form
    // can blank out (due date, work order, hold) must be sent as deleteField().
    const entries = Object.entries(data);
    if (existing) {
      const patch = Object.fromEntries(entries.map(([k, v]) => [k, v === undefined ? deleteField() : v]));
      await updateDoc(doc(db, 'maintenance', existing.itemId), { ...patch, updatedAt: serverTimestamp() });
    } else {
      await addDoc(collection(db, 'maintenance'), {
        ...Object.fromEntries(entries.filter(([, v]) => v !== undefined)),
        aircraftId: acId,
        lastAlertLevel: 0,
        createdAt: serverTimestamp(),
      });
    }
    await syncAnnual({ label: data.label ?? existing?.label }, data.dueDate);
    setEditing(null);
  }

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
        <div className="flex items-center gap-2">
          <Wrench size={18} className="text-navy-700" />
          <h2 className="font-display text-lg font-semibold text-navy-900">Maintenance &amp; airworthiness</h2>
        </div>
        <button
          className="btn-secondary inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm"
          onClick={() => setEditing('new')}
        >
          <Plus size={15} /> Add item
        </button>
      </div>

      {/* Engine time since major overhaul. Derived from the tach reading at overhaul
          rather than stored as a running total, so it can never drift out of date.
          Shows nothing but a prompt when the overhaul point hasn't been recorded —
          a made-up SMOH on a 1961 airframe would be worse than no SMOH. */}
      <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
        <div className="text-[11px] uppercase tracking-[0.07em] text-ink-subtle">Engine</div>
        {aircraft.engineOverhaulTach == null ? (
          <p className="mt-1 text-sm text-ink-muted">
            {aircraft.engineMake} {aircraft.engineModel}
            {aircraft.engineSerial ? ` · SN ${aircraft.engineSerial}` : ''} — SMOH not recorded.
            Add the tach reading at overhaul to track it.
          </p>
        ) : (
          (() => {
            const smoh = +(tach - aircraft.engineOverhaulTach).toFixed(1);
            const tbo = aircraft.engineTboHours;
            const left = tbo != null ? +(tbo - smoh).toFixed(1) : null;
            const pct = tbo ? Math.min(100, Math.max(0, (smoh / tbo) * 100)) : 0;
            const warn = left != null && left <= 200;
            return (
              <>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-display text-2xl font-bold text-navy-900">{smoh.toFixed(1)}</span>
                  <span className="text-sm text-ink-muted">hours SMOH</span>
                  {tbo != null && (
                    <span className={`text-sm ${warn ? 'font-semibold text-[#B42318]' : 'text-ink-muted'}`}>
                      · {left} to {tbo}-hr TBO
                    </span>
                  )}
                </div>
                {tbo != null && (
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${warn ? 'bg-[#B42318]' : 'bg-brand'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
                <p className="mt-2 text-xs text-ink-muted">
                  {aircraft.engineMake} {aircraft.engineModel}
                  {aircraft.engineSerial ? ` · SN ${aircraft.engineSerial}` : ''}
                  {' · overhauled at '}
                  {aircraft.engineOverhaulTach} tach
                  {aircraft.engineOverhaulDate ? ` (${aircraft.engineOverhaulDate})` : ''}
                  {' · tach now '}
                  {tach}
                </p>
              </>
            );
          })()
        )}
      </div>

      {sorted.length === 0 ? (
        <div className="p-6 text-center text-sm text-ink-muted">
          No items tracked yet. Add your annual, 100-hour, oil change, transponder/static, ELT, and ADs —
          you’ll get an email as each one approaches.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {sorted.map((item) => {
            const { level } = mxStatus(item, tach);
            const deferred = isDeferred(item);
            return (
              <li key={item.itemId} className="flex flex-wrap items-center gap-3 px-5 py-3 sm:px-6">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    deferred ? 'bg-slate-100 text-slate-600' : TONE_CLS[MX_TONE[level]]
                  }`}
                >
                  {deferred ? 'Deferred' : MX_LABEL[level]}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-navy-900">
                    {item.label}
                    {item.isAirworthiness && (
                      <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-navy-700">
                        Airworthiness
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted">
                    {deferred && (
                      <span className="text-slate-600">
                        Held until {item.deferredUntil}
                        {item.deferReason ? ` — ${item.deferReason}` : ''} ·{' '}
                      </span>
                    )}
                    {mxSummary(item, tach)}
                    {item.dueDate ? ` · due ${item.dueDate}` : ''}
                    {typeof item.dueHobbs === 'number' ? ` · @ ${item.dueHobbs.toFixed(1)} tach` : ''}
                    {item.lastDoneDate ? ` · last ${item.lastDoneDate}` : ''}
                  </div>
                  {item.notes && <div className="mt-1 text-xs leading-snug text-ink-subtle">{item.notes}</div>}
                  {item.docUrl && (
                    <a
                      href={item.docUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-navy-700 hover:underline"
                    >
                      <FileText size={12} /> View work order
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button className="rounded p-1.5 text-emerald-700 hover:bg-emerald-50" title="Mark complete" onClick={() => markDone(item)}>
                    <Check size={16} />
                  </button>
                  <button className="rounded p-1.5 text-navy-700 hover:bg-sky-50" title="Edit" onClick={() => setEditing(item)}>
                    <Pencil size={15} />
                  </button>
                  <button className="rounded p-1.5 text-red-600 hover:bg-red-50" title="Delete" onClick={() => remove(item)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <MaintenanceForm
          item={editing === 'new' ? undefined : editing}
          currentTach={tach}
          aircraftId={acId}
          demoMode={demoMode}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function MaintenanceForm({
  item,
  currentTach,
  aircraftId,
  demoMode,
  onClose,
  onSave,
}: {
  item?: MaintenanceItemDoc;
  currentTach: number;
  aircraftId: string;
  demoMode?: boolean;
  onClose: () => void;
  onSave: (data: Partial<MaintenanceItemDoc>, existing?: MaintenanceItemDoc) => void;
}) {
  const [label, setLabel] = useState(item?.label ?? '');
  const [track, setTrack] = useState<MaintenanceTrack>(item?.track ?? 'date');
  const [dueDate, setDueDate] = useState(item?.dueDate ?? '');
  const [dueHobbs, setDueHobbs] = useState<string>(item?.dueHobbs != null ? String(item.dueHobbs) : '');
  const [intervalDays, setIntervalDays] = useState<string>(item?.intervalDays != null ? String(item.intervalDays) : '');
  const [intervalHours, setIntervalHours] = useState<string>(item?.intervalHours != null ? String(item.intervalHours) : '');
  const [isAirworthiness, setIsAirworthiness] = useState(item?.isAirworthiness ?? false);
  const [deferredUntil, setDeferredUntil] = useState(item?.deferredUntil ?? '');
  const [deferReason, setDeferReason] = useState(item?.deferReason ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [docUrl, setDocUrl] = useState(item?.docUrl ?? '');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');

  function applyPreset(p: (typeof MX_PRESETS)[number]) {
    setLabel(p.label);
    setTrack(p.track);
    setIsAirworthiness(p.isAirworthiness);
    setIntervalDays(p.intervalDays != null ? String(p.intervalDays) : '');
    setIntervalHours(p.intervalHours != null ? String(p.intervalHours) : '');
  }

  const showDate = track === 'date' || track === 'both';
  const showHours = track === 'hours' || track === 'both';
  // Airworthiness items can never be deferred — an expired annual or pitot/static
  // is a legal grounding, not a scheduling preference.
  const canDefer = !isAirworthiness;

  async function uploadDoc(file: File) {
    setUploadErr('');
    if (file.size > 15 * 1024 * 1024) {
      setUploadErr('File too large (max 15 MB).');
      return;
    }
    setUploading(true);
    try {
      if (demoMode || !storage) {
        // Demo: local preview only — nothing is persisted.
        setDocUrl(URL.createObjectURL(file));
      } else {
        const safe = file.name.replace(/[^A-Za-z0-9._-]/g, '_');
        const sref = storageRef(storage, `maintenance/${aircraftId}/${Date.now()}-${safe}`);
        await uploadBytes(sref, file);
        setDocUrl(await getDownloadURL(sref));
      }
    } catch {
      setUploadErr('Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  }

  function submit() {
    const data: Partial<MaintenanceItemDoc> = { label: label.trim(), track, isAirworthiness, notes: notes.trim() };
    data.dueDate = showDate ? dueDate || undefined : undefined;
    data.dueHobbs = showHours && dueHobbs !== '' ? Number(dueHobbs) : undefined;
    data.intervalDays = intervalDays !== '' ? Number(intervalDays) : undefined;
    data.intervalHours = intervalHours !== '' ? Number(intervalHours) : undefined;
    data.docUrl = docUrl || undefined;
    data.deferredUntil = canDefer && deferredUntil ? deferredUntil : undefined;
    data.deferReason = canDefer && deferredUntil && deferReason.trim() ? deferReason.trim() : undefined;
    if (!item) data.createdAt = Timestamp.now();
    onSave(data, item);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 p-4 backdrop-blur-sm">
      <div className="card relative max-h-[90vh] w-full max-w-lg overflow-y-auto p-6">
        <ModalClose onClose={onClose} />
        <h3 className="pr-10 font-display text-lg font-semibold text-navy-900">
          {item ? 'Edit' : 'Add'} maintenance item
        </h3>

        {!item && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {MX_PRESETS.map((p) => (
              <button key={p.label} type="button" onClick={() => applyPreset(p)} className="rounded-full border border-navy-900/15 px-2.5 py-1 text-xs text-navy-700 hover:bg-sky-50">
                {p.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-4">
          <div>
            <label className="label">Item</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Annual Inspection" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Track by</label>
              <select className="input" value={track} onChange={(e) => setTrack(e.target.value as MaintenanceTrack)}>
                <option value="date">Calendar date</option>
                <option value="hours">Tach hours</option>
                <option value="both">Both</option>
              </select>
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input type="checkbox" checked={isAirworthiness} onChange={(e) => setIsAirworthiness(e.target.checked)} />
              Grounds the plane if overdue
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {showDate && (
              <div>
                <label className="label">Due date</label>
                <DateField value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            )}
            {showHours && (
              <div>
                <label className="label">Due at tach (now {currentTach.toFixed(1)})</label>
                <input type="number" step="0.1" className="input" value={dueHobbs} onChange={(e) => setDueHobbs(e.target.value)} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Repeat every (days)</label>
              <input type="number" className="input" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} placeholder="365" />
            </div>
            <div>
              <label className="label">Repeat every (hours)</label>
              <input type="number" className="input" value={intervalHours} onChange={(e) => setIntervalHours(e.target.value)} placeholder="100" />
            </div>
          </div>
          {canDefer && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Hold until (optional)</label>
                  <DateField value={deferredUntil} onChange={(e) => setDeferredUntil(e.target.value)} />
                </div>
                <div>
                  <label className="label">Why it's held</label>
                  <input
                    className="input"
                    value={deferReason}
                    onChange={(e) => setDeferReason(e.target.value)}
                    placeholder="doing it at the annual"
                    disabled={!deferredUntil}
                  />
                </div>
              </div>
              <p className="mt-2 text-xs text-ink-muted">
                Pilots won't see this item on the booking screen and you won't get alert emails about it
                until this date passes — then it goes back to normal on its own.
              </p>
            </div>
          )}
          <div>
            <label className="label">Notes (optional)</label>
            <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div>
            <label className="label">Work order / photo (optional)</label>
            {docUrl ? (
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-sky-50/40 px-3 py-2 text-sm">
                <FileText size={15} className="shrink-0 text-navy-700" />
                <a href={docUrl} target="_blank" rel="noopener noreferrer" className="flex-1 truncate font-medium text-navy-700 hover:underline">
                  View attached file
                </a>
                <button
                  type="button"
                  onClick={() => setDocUrl('')}
                  className="rounded p-1 text-ink-muted hover:bg-slate-100 hover:text-red-600"
                  title="Remove attachment"
                >
                  <X size={15} />
                </button>
              </div>
            ) : (
              <FilePicker
                accept="image/*,application/pdf"
                disabled={uploading}
                photoLabel={uploading ? 'Uploading…' : 'Take photo'}
                chooseLabel="Attach photo or PDF"
                onFiles={(files) => {
                  if (files[0]) uploadDoc(files[0]);
                }}
              />
            )}
            {uploadErr && <p className="mt-1 text-xs text-red-600">{uploadErr}</p>}
          </div>

          <p className="text-xs text-ink-muted">
            “Mark complete” later logs today + the current Hobbs and auto-rolls the next due using these repeat
            intervals.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={submit} disabled={!label.trim()} className="btn-primary">Save item</button>
        </div>
      </div>
    </div>
  );
}
